import "./enhancedButton.scss";
import { Link } from "react-router-dom";
import React from "react";

type ButtonSize = "sm" | "md" | "lg";
type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline-dark"
  | "outline-light"
  | "text";
type ButtonState = "default" | "disabled";
type ButtonType = "submit" | "button" | "reset" | undefined;

type ButtonProps = {
  title: string;
  id: string;
  to?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  state?: ButtonState;
  disabled?: boolean;
  onClick?: any;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  type?: ButtonType;
  className?: string;
  isLoading?: boolean;
  style?: React.CSSProperties;
};

const EnhancedButton: React.FC<ButtonProps> = ({
  title,
  id,
  to,
  size = "md",
  variant = "primary",
  state = "default",
  disabled = false,
  onClick,
  icon,
  iconPosition = "left",
  type,
  className,
  isLoading,
  style,
}) => {
  const isDisabled = disabled || state === "disabled";

  const getSizeClasses = (size: ButtonSize): string => {
    switch (size) {
      case "sm":
        return "btn-sm";
      case "lg":
        return "btn-lg";
      case "md":
      default:
        return "btn-md";
    }
  };

  const getVariantClasses = (variant: ButtonVariant): string => {
    const baseClasses = "btn";

    if (isDisabled) {
      return `${baseClasses} btn-disabled`;
    }

    switch (variant) {
      case "primary":
        return `${baseClasses} btn-primary`;
      case "secondary":
        return `${baseClasses} btn-secondary`;
      case "outline-dark":
        return `${baseClasses} btn-outline-dark`;
      case "outline-light":
        return `${baseClasses} btn-outline`;
      case "text":
        return `${baseClasses} btn-text`;
      default:
        return `${baseClasses} btn-primary`;
    }
  };

  const sizeClasses = getSizeClasses(size);
  const variantClasses = getVariantClasses(variant);

  const buttonContent = (
    <button
      className={`${variantClasses} ${sizeClasses} ${className || ""}`}
      id={id}
      data-testid={id}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      type={type}
      style={style}
    >
      <span className="btn-content">
        {icon && iconPosition === "left" && (
          <span className="btn-icon btn-icon-left">{icon}</span>
        )}
        <span>{title}</span>
        {icon && iconPosition === "right" && (
          <span className="btn-icon btn-icon-right">{icon}</span>
        )}
      </span>
    </button>
  );

  if (to && !isDisabled) {
    return (
      <Link to={to} className="btn-link-wrapper" data-testid={`${id}-link`}>
        {buttonContent}
      </Link>
    );
  }

  return buttonContent;
};

export default EnhancedButton;
